import { MouseEvent, ReactNode, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  getAllBulkImports,
  getBulkImportTypes,
  UserKeys,
} from '../../../api/users-api';
import { BulkImportModel, BulkImportType } from '../../../../types';
import SimpleDrawer from '../../../shared/simple-drawer';
import ModelDetailsDisplay from '../../../shared/model-details-display';
import AvatarChip from '../../../shared/avatar-chip';
import HtmlPreview from '../../../shared/html-preview';
import { DisabledTooltipMenuItem } from '../../../shared/datagrids/tooltip-grid-actions-cell-item';
import { useBulkOpActions } from './hooks/use-bulk-op-actions';
import { useUserSettings } from '../../../utils/use-user-settings';
import { isAbleToSeeUIElement } from '../../../utils/roles/client-roles';
import DeleteBulkOpsDialog from './delete-bulk-ops-dialog';
import RefreshBulkOpsDialog from './refresh-bulk-ops-dialog';
import RunBulkOpsDialog from './run-bulk-ops-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import dayjs from 'dayjs';

import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import Delete from '@mui/icons-material/Delete';
import MoreVert from '@mui/icons-material/MoreVert';
import Pageview from '@mui/icons-material/Pageview';
import PlayArrow from '@mui/icons-material/PlayArrow';
import RestorePage from '@mui/icons-material/RestorePage';
import Update from '@mui/icons-material/Update';

interface BulkOpsDrawerContentProps {
  selectedBulkOp: BulkImportModel;
  bulkImportTypes: BulkImportType[];
  toolsRoles: string[];
  loggedInUserEmail: string;
  onClose: () => void;
}

const sourceChipType = (
  sourceType: BulkImportModel['sourceType']
): 'User' | 'Group' | 'Domain' | 'gPanel Role' | 'OrgUnit' | 'Unknown' => {
  switch (sourceType) {
    case 'User':
      return 'User';
    case 'Group':
      return 'Group';
    case 'OrgUnit':
      return 'OrgUnit';
    case 'Domain':
      return 'Domain';
    case 'gPanel_Role':
      return 'gPanel Role';
    default:
      return 'Unknown';
  }
};

const formatSourceType = (
  sourceType: BulkImportModel['sourceType']
): string => {
  switch (sourceType) {
    case 'All_Users':
      return 'All Users';
    case 'All_Groups':
      return 'All Groups';
    case 'gPanel_Role':
      return 'gPanel Role';
    case 'Mobile_Device':
      return 'Mobile Device';
    case null:
      return 'N/A';
    default:
      return sourceType;
  }
};

export function BulkOpsDrawerContent({
  selectedBulkOp,
  bulkImportTypes,
  toolsRoles,
  loggedInUserEmail,
  onClose,
}: Readonly<BulkOpsDrawerContentProps>) {
  const [showAllColumns, setShowAllColumns] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openRefreshDialog, setOpenRefreshDialog] = useState(false);
  const [openRunDialog, setOpenRunDialog] = useState(false);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const menuOpen = Boolean(anchorEl);
  const MAX_CHIPS = 8;

  const {
    hasBulkOpsPermission,
    getPermissionDisabledReason,
    handleViewSpreadsheet,
    runDirectBulkOp,
    isRunningBulkOp,
    changeLogRoles,
  } = useBulkOpActions();

  const bulkImportType = bulkImportTypes.find(
    (type) => type.name === selectedBulkOp.bulkImport
  );

  const handleMenuClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const canDelete = hasBulkOpsPermission(toolsRoles);
  const refreshDisabledReason = getPermissionDisabledReason(
    toolsRoles,
    bulkImportType?.role || ''
  );
  const canRefresh = !refreshDisabledReason;
  const canRefreshSpreadsheet =
    selectedBulkOp.path === 'guideMe' && bulkImportType?.usesSpreadsheet;

  const runPermissionDisabledReason = getPermissionDisabledReason(
    toolsRoles,
    bulkImportType?.role || ''
  );
  const runDisabledReason = runPermissionDisabledReason
    ? runPermissionDisabledReason
    : selectedBulkOp.status === 'RUNNING'
    ? 'This bulk op is currently running.'
    : undefined;
  const canRun = !runDisabledReason;

  const handleRunBulkOp = () => {
    handleMenuClose();
    if (bulkImportType?.usesSpreadsheet) {
      setOpenRunDialog(true);
    } else if (
      isAbleToSeeUIElement(changeLogRoles, bulkImportType?.role || '')
    ) {
      setOpenChangeLogDialog(true);
    } else {
      runDirectBulkOp({ bulkOp: selectedBulkOp, changeLogMessage: '' });
    }
  };

  const basicInfoData: Record<string, ReactNode> = useMemo(() => {
    const emailChips = selectedBulkOp.notifyEmails?.length ? (
      <Stack direction="row" gap={1} flexWrap="wrap">
        {selectedBulkOp.notifyEmails.map((email) => (
          <AvatarChip key={email} email={email} type="User" size="small" />
        ))}
      </Stack>
    ) : null;

    const data: Record<string, ReactNode> = {
      'Created By': selectedBulkOp.createdBy ? (
        <AvatarChip email={selectedBulkOp.createdBy} type="User" size="small" />
      ) : (
        'N/A'
      ),
      'Bulk Import Type': bulkImportType?.niceName || selectedBulkOp.bulkImport,
      Description: selectedBulkOp.description || 'N/A',
      'Run Status':
        selectedBulkOp.status === 'RUNNING'
          ? 'Bulk Op Running...'
          : selectedBulkOp.status === 'COMPLETED'
          ? 'Finished Running'
          : 'Idle',
    };

    if (bulkImportType?.usesSpreadsheet) {
      data['Last Export Date'] = selectedBulkOp.lastExportUpdate
        ? dayjs(selectedBulkOp.lastExportUpdate).format('MMM D, YYYY, h:mm A')
        : 'Not Yet Exported';
    }

    if (emailChips) data['Email Notifications'] = emailChips;

    return data;
  }, [selectedBulkOp, bulkImportType]);

  const configurationData: Record<string, ReactNode> | null = useMemo(() => {
    const data: Record<string, ReactNode> = {};

    if (selectedBulkOp.path) {
      data['Path'] =
        selectedBulkOp.path === 'guideMe'
          ? 'Guided Export'
          : 'Advanced Operation';
    }

    if (selectedBulkOp.spreadsheetId) {
      data['Spreadsheet Id'] = selectedBulkOp.spreadsheetId;
    }

    if (selectedBulkOp.resourceIds?.length) {
      data['Resource Ids'] = (
        <Stack direction="row" gap={1} flexWrap="wrap">
          {selectedBulkOp.resourceIds.map((resourceId) => (
            <AvatarChip
              key={resourceId}
              email={resourceId}
              type="Unknown"
              size="small"
            />
          ))}
        </Stack>
      );
    }

    if (selectedBulkOp.startDate) {
      data['Start Date'] = dayjs(selectedBulkOp.startDate).format('MM/DD/YYYY');
    }

    if (selectedBulkOp.endDate) {
      data['End Date'] = dayjs(selectedBulkOp.endDate).format('MM/DD/YYYY');
    }

    return Object.keys(data).length > 0 ? data : null;
  }, [selectedBulkOp]);

  const columnsData: Record<string, ReactNode> | null = useMemo(() => {
    const columns = selectedBulkOp.columns || [];
    if (!columns.length) return null;

    return {
      'Selected Columns': (
        <Stack gap={1}>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {columns
              .slice(0, showAllColumns ? columns.length : MAX_CHIPS)
              .map((column) => (
                <AvatarChip
                  key={column.columnName}
                  email={column.columnName}
                  type="Unknown"
                  size="small"
                />
              ))}
          </Stack>
          {columns.length > MAX_CHIPS && (
            <Typography
              component="button"
              onClick={() => setShowAllColumns(!showAllColumns)}
              sx={{
                alignSelf: 'flex-start',
                color: 'primary.main',
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                p: 0,
                fontSize: 13,
              }}
            >
              {showAllColumns
                ? 'Show less'
                : `+${columns.length - MAX_CHIPS} more`}
            </Typography>
          )}
        </Stack>
      ),
    };
  }, [selectedBulkOp.columns, showAllColumns]);

  const sourcesData: Record<string, ReactNode> | null = useMemo(() => {
    const sourceType = selectedBulkOp.sourceType;
    const sources = selectedBulkOp.sources || [];

    if (!sourceType && !sources.length && !selectedBulkOp.allSource)
      return null;

    const data: Record<string, ReactNode> = {};

    if (sourceType) {
      data['Source Type'] = formatSourceType(sourceType);
    }

    if (selectedBulkOp.allSource) {
      data['Sources'] = 'All';
    } else if (sources.length > 0) {
      const chipType = sourceChipType(sourceType);
      data['Sources'] = (
        <Stack direction="row" gap={1} flexWrap="wrap">
          {sources.map((source) => (
            <AvatarChip
              key={source}
              email={source}
              type={chipType}
              size="small"
            />
          ))}
        </Stack>
      );
    }

    return Object.keys(data).length > 0 ? data : null;
  }, [selectedBulkOp]);

  const groupConfigData: Record<string, ReactNode> | null = useMemo(() => {
    const nestedGroups = selectedBulkOp.includeNestedGroups || [];
    if (!nestedGroups.length && selectedBulkOp.bulkImport !== 'groupImport')
      return null;

    const data: Record<string, ReactNode> = {};

    if (nestedGroups.length > 0) {
      data['Included Nested Groups'] = (
        <Stack direction="row" gap={1} flexWrap="wrap">
          {nestedGroups.map((group) => (
            <AvatarChip key={group} email={group} type="Group" size="small" />
          ))}
        </Stack>
      );
    }

    if (selectedBulkOp.bulkImport === 'groupImport') {
      data['Auto Create Groups'] = selectedBulkOp.createGroups ? (
        <Check fontSize="small" sx={{ color: 'text.secondary' }} />
      ) : (
        <Close fontSize="small" sx={{ color: 'text.secondary' }} />
      );
    }

    return Object.keys(data).length > 0 ? data : null;
  }, [selectedBulkOp]);

  const vacationResponderData: Record<string, ReactNode> | null =
    useMemo(() => {
      if (selectedBulkOp.bulkImport !== 'usersVacationResponder') return null;
      const vacation = selectedBulkOp.vacationResponderData;
      if (!vacation) return null;

      const data: Record<string, ReactNode> = {};

      if (vacation.startTime) {
        data['Start Date'] = dayjs(vacation.startTime).format('MM/DD/YYYY');
      }
      if (vacation.endTime) {
        data['End Date'] = dayjs(vacation.endTime).format('MM/DD/YYYY');
      }
      if (vacation.responseSubject) {
        data['Subject'] = vacation.responseSubject;
      }
      if (vacation.responseBodyHtml) {
        data['Preview'] = (
          <HtmlPreview
            title="Vacation Responder Preview"
            html={vacation.responseBodyHtml}
            showTitle={false}
          />
        );
      }

      return Object.keys(data).length > 0 ? data : null;
    }, [selectedBulkOp]);

  return (
    <SimpleDrawer title="Bulk Operation Details" onClose={onClose}>
      <Stack pl={2} pr={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {selectedBulkOp.name || 'N/A'}
          </Typography>
          <Tooltip title="Actions">
            <IconButton
              onClick={handleMenuClick}
              id="bulk-op-details-menu-button"
              size="small"
            >
              <MoreVert />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            elevation={1}
            onClose={handleMenuClose}
            MenuListProps={{
              'aria-labelledby': 'bulk-op-details-menu-button',
            }}
          >
            <DisabledTooltipMenuItem
              onClick={handleRunBulkOp}
              disabled={!canRun || isRunningBulkOp}
              data-disabled-reason={runDisabledReason}
            >
              <ListItemIcon>
                {isRunningBulkOp || selectedBulkOp.status === 'RUNNING' ? (
                  <CircularProgress size={20} />
                ) : (
                  <PlayArrow fontSize="small" />
                )}
              </ListItemIcon>
              Run
            </DisabledTooltipMenuItem>
            {bulkImportType?.usesSpreadsheet && (
              <DisabledTooltipMenuItem
                onClick={() => {
                  handleMenuClose();
                  handleViewSpreadsheet(selectedBulkOp, loggedInUserEmail);
                }}
              >
                <ListItemIcon>
                  <Pageview fontSize="small" />
                </ListItemIcon>
                View Spreadsheet
              </DisabledTooltipMenuItem>
            )}
            {canRefreshSpreadsheet && (
              <DisabledTooltipMenuItem
                onClick={() => {
                  handleMenuClose();
                  setOpenRefreshDialog(true);
                }}
                disabled={!canRefresh}
                data-disabled-reason={refreshDisabledReason}
              >
                <ListItemIcon>
                  <RestorePage fontSize="small" />
                </ListItemIcon>
                Refresh Spreadsheet Data
              </DisabledTooltipMenuItem>
            )}
            <DisabledTooltipMenuItem
              onClick={() => {
                handleMenuClose();
                setOpenDeleteDialog(true);
              }}
              disabled={!canDelete}
              data-disabled-reason={
                canDelete
                  ? undefined
                  : 'You do not have the required permission(s) to delete this bulk operation.'
              }
            >
              <ListItemIcon>
                <Delete fontSize="small" />
              </ListItemIcon>
              Delete
            </DisabledTooltipMenuItem>
          </Menu>
        </Stack>

        {openDeleteDialog && (
          <DeleteBulkOpsDialog
            open={openDeleteDialog}
            handleClose={() => setOpenDeleteDialog(false)}
            selectedBulkOps={[selectedBulkOp]}
            onDeleteSuccess={onClose}
          />
        )}

        {openRefreshDialog && (
          <RefreshBulkOpsDialog
            open={openRefreshDialog}
            handleClose={() => setOpenRefreshDialog(false)}
            selectedBulkOp={selectedBulkOp}
          />
        )}

        {openRunDialog && (
          <RunBulkOpsDialog
            open={openRunDialog}
            handleClose={() => setOpenRunDialog(false)}
            selectedBulkOp={selectedBulkOp}
            loggedInUserEmail={loggedInUserEmail}
            bulkImportTypes={bulkImportTypes}
            changeLogRoles={changeLogRoles}
          />
        )}

        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={() => {
            runDirectBulkOp({ bulkOp: selectedBulkOp, changeLogMessage });
            setOpenChangeLogDialog(false);
            setChangeLogMessage('');
          }}
          mutateLoading={isRunningBulkOp}
        />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {selectedBulkOp.description || 'No description provided.'}
        </Typography>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1, sm: 2 }}
          sx={{ mb: 2, alignItems: 'center' }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ color: 'text.secondary' }}
          >
            <Update fontSize="small" />
            <Typography variant="body2" color="inherit">
              Last Run Date:{' '}
              {selectedBulkOp.lastRan
                ? dayjs(selectedBulkOp.lastRan).format('MMM D, YYYY, h:mm A')
                : 'Never Ran'}
            </Typography>
          </Stack>
          <Typography sx={{ px: 1, color: 'text.secondary', fontSize: 14 }}>
            •
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ color: 'text.secondary' }}
          >
            <Check fontSize="small" />
            <Typography variant="body2" color="inherit">
              Created Date:{' '}
              {selectedBulkOp.createdDate
                ? dayjs(selectedBulkOp.createdDate).format(
                    'MMM D, YYYY, h:mm A'
                  )
                : 'N/A'}
            </Typography>
          </Stack>
        </Stack>

        <ModelDetailsDisplay title="Basic Information" data={basicInfoData} />
        {configurationData && (
          <ModelDetailsDisplay title="Configuration" data={configurationData} />
        )}
        {columnsData && (
          <ModelDetailsDisplay title="Columns" data={columnsData} />
        )}
        {sourcesData && (
          <ModelDetailsDisplay title="Sources" data={sourcesData} />
        )}
        {groupConfigData && (
          <ModelDetailsDisplay
            title="Group Configuration"
            data={groupConfigData}
          />
        )}
        {vacationResponderData && (
          <ModelDetailsDisplay
            title="Vacation Responder"
            data={vacationResponderData}
          />
        )}
      </Stack>
    </SimpleDrawer>
  );
}

/**
 * Global, self-contained bulk operation detail drawer.
 * Driven entirely by the `?selectedBulkOp=<id>` URL search param.
 * Place this once at the App level — no props required.
 */
export default function BulkOpsDrawerDetailsOld() {
  const [searchParams] = useSearchParams();
  const { closeDrawer } = useBulkOpActions();
  const { toolsRoles, loggedInUser } = useUserSettings();

  const selectedBulkOpId = searchParams.get('selectedBulkOp');

  const { data: bulkImportModels } = useQuery({
    queryKey: [UserKeys.BulkOps],
    queryFn: () => getAllBulkImports(),
    enabled: !!selectedBulkOpId,
  });

  const { data: bulkImportTypes } = useQuery({
    queryKey: [UserKeys.BulkOpsTypes],
    queryFn: () => getBulkImportTypes(),
    enabled: !!selectedBulkOpId,
  });

  const selectedBulkOp =
    bulkImportModels?.find((m) => m.id.toString() === selectedBulkOpId) ?? null;

  return (
    <Drawer
      anchor="right"
      open={!!selectedBulkOp}
      onClose={closeDrawer}
      sx={{ '& .MuiDrawer-paper': { width: { xs: '100vw', md: '45%' } } }}
    >
      {selectedBulkOp && (
        <BulkOpsDrawerContent
          selectedBulkOp={selectedBulkOp}
          bulkImportTypes={bulkImportTypes || []}
          toolsRoles={toolsRoles}
          loggedInUserEmail={loggedInUser}
          onClose={closeDrawer}
        />
      )}
    </Drawer>
  );
}
